import { createAction, createAsyncAction } from "typesafe-actions";
import {
  AllTimeSummary,
  ApproveRedemptionRequestDto,
  ApproveRedemptionRequestSuccess,
  BadgeDefinition,
  CreateRedemptionRequestDto,
  CreateRedemptionRequestSuccess,
  DenyRedemptionRequestDto,
  DenyRedemptionRequestSuccess,
  EarnedBadge,
  GetLeaderboardQuery,
  GetPendingRedemptionRequestsQuery,
  GetPendingRedemptionRequestsSuccess,
  GetRedemptionLedgerQuery,
  GetRedemptionLedgerSuccess,
  GivingActivity,
  LEADERBOARD_BOARD,
  LEADERBOARD_PERIOD,
  LeaderboardCrown,
  LeaderboardResultsResponse,
  OrgRedemptionStats,
  RedemptionRequestDecisionFailure,
  RewardItem,
  SparksSummary,
  SuggestedCollaborator,
  UserBadgesResponse,
  UserRedemptionSummary,
} from "@shared/models";
import { WithCallback } from "@shared/interfaces";

import { PersonSparks } from "../../interfaces";
import { SparksActionTypes } from "./constants";

/**
 * D5: the failure payload is typed `unknown` via a payload-creator tuple (`[FAILURE, (e: unknown) =>
 * e]`), NOT the three-generic `<Payload, Success, unknown>()` form — the latter silently resolves
 * `.failure`'s payload to `any` under this repo's typesafe-actions (measured with tsc; see the
 * compile-time check in reducer.test.tsx).
 *
 * All THREE arms use a payload-creator tuple, not just the failure one: mixing a plain `TType` string
 * for request/success with a tuple for failure does not compile against this repo's typesafe-actions
 * (measured directly — `createAsyncAction(REQ, SUCCESS, [FAILURE, creator])<Payload, Success>()`
 * throws `TS2344: Type 'number' does not satisfy the constraint '[undefined, undefined]'`, because the
 * request/success payload types default to `undefined` once ANY arm forces the tuple-creator overload,
 * and the call-site generics are then constrained against that `undefined`, not free to be `Payload`/
 * `Success`). Request/success payload types come from their own identity creator's parameter type
 * instead, so no call-site generics are needed at all.
 *
 * `request`'s interceptor rejects with the raw wire body (`{ code, message }`), never an `Error`
 * instance, so narrowing to `Error` here would be a false cast — `unknown` is left deliberately: no UI
 * consumes this slice yet (D8), so there is nothing to decide what a narrower shape should be. Phase
 * 34+ narrows at its own point of use.
 */
export const getSparksSummary = createAsyncAction(
  [SparksActionTypes.GET_SPARKS_SUMMARY, (userId: number) => userId],
  [SparksActionTypes.GET_SPARKS_SUMMARY_SUCCESS, (summary: SparksSummary) => summary],
  [SparksActionTypes.GET_SPARKS_SUMMARY_FAILURE, (e: unknown) => e],
)();

export const getUserBadges = createAsyncAction(
  [SparksActionTypes.GET_USER_BADGES, (userId: number) => userId],
  [SparksActionTypes.GET_USER_BADGES_SUCCESS, (badges: UserBadgesResponse) => badges],
  [SparksActionTypes.GET_USER_BADGES_FAILURE, (e: unknown) => e],
)();

// D2 (923-core-context context/engineering/spark-rewards/phase-36-implementation-plan.md): no request payload -- the catalog doesn't vary by user, so
// `request` is a tuple creator returning `undefined`, following the same all-tuple-arms convention
// the two triads above use in this file (mixing a plain-string arm with a tuple arm does not compile
// under this repo's typesafe-actions, per the file-level comment above). Call site is
// `SparksActions.getBadgeDefinitions.request()` with no argument.
export const getBadgeDefinitions = createAsyncAction(
  [SparksActionTypes.GET_BADGE_DEFINITIONS, () => undefined],
  [SparksActionTypes.GET_BADGE_DEFINITIONS_SUCCESS, (definitions: BadgeDefinition[]) => definitions],
  [SparksActionTypes.GET_BADGE_DEFINITIONS_FAILURE, (e: unknown) => e],
)();

// Phase 37 D2/D3: the success payload is tagged with the `userId` it was fetched for
// (`{ userId, badges }`), not a bare `EarnedBadge[]` — this is what lets a consumer render safely
// regardless of fetch timing (see reducer.tsx/UserRecognitionsContainer.tsx's own comments). All
// three arms use the tuple-creator form, matching this file's own convention above.
export const getProfileBadges = createAsyncAction(
  [SparksActionTypes.GET_PROFILE_BADGES, (userId: number) => userId],
  [SparksActionTypes.GET_PROFILE_BADGES_SUCCESS, (payload: { userId: number; badges: EarnedBadge[] }) => payload],
  [SparksActionTypes.GET_PROFILE_BADGES_FAILURE, (e: unknown) => e],
)();

/**
 * Phase 38 D5 (REVISED after plan-reviewer round 1): a SEPARATE triad from `getLeaderboardCrowns`
 * below, even though both call the single `api.getLeaderboard` method -- the ranked board
 * (received/given) and the crowns row are two independent fetches that must not cancel each other.
 * `saga.tsx`'s workers are every one `takeLatest`-registered on the action's TYPE; dispatching the
 * SAME action type twice in quick succession (once for the board, once for crowns) would make
 * `takeLatest` cancel the first in-flight task the instant the second lands, and the ranked board
 * would never load. Mirrors `getUserBadges`/`getProfileBadges`'s own precedent above for two
 * independent fetches over one api method. Do NOT collapse this back into one triad -- see the
 * plan's own "Conventions the build must follow".
 *
 * L2 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): the success payload WIDENED from a bare `LeaderboardEntry[]`
 * to `{ board, period, results, viewer }` together, so the tab can render its own pinned "You" row
 * from `viewer` instead of throwing it away on arrival.
 *
 * 🚨 `board`/`period` ride along because the response already carries them (`leaderboard.schemas.ts`'s
 * own `rankedBoardResultFields` sends both on every received/given response) and WITHOUT them nothing
 * downstream can tell which board/period a retained `results`/`viewer` pair describes. That is not
 * hypothetical: `setLeaderboardBoard` clears no data, and `.failure` deliberately keeps the stale pair,
 * so a failed fetch after a toggle click used to leave the PREVIOUS board's podium, rows and pinned
 * "You" row rendered under the NEW board's label -- the viewer reading their received rank as their
 * given rank. `LeaderboardsContainer` now renders the pair only while these two fields match the live
 * toggles. This is a WIDER `Pick` than `getOverviewLeaderboard` below, which needs no such check: that
 * card owns its board/period in local `useState`, so its data and its labels re-render together.
 *
 * All four fields come from ONE response and are written by this ONE action (reducer.tsx), so they can
 * never describe two different fetches.
 *
 * L7 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md) widens the `Pick` again to include `window` -- the SAME
 * response already carries it (`rankedBoardResultFields`), so the reset line's own boundary rides
 * along with the group it describes rather than a second fetch. `pendingApproval`, also on that same
 * response, is deliberately NOT picked -- see `LeaderboardResultsResponse`'s own model comment for why.
 */
export const getLeaderboard = createAsyncAction(
  [SparksActionTypes.GET_LEADERBOARD, (query: GetLeaderboardQuery) => query],
  [
    SparksActionTypes.GET_LEADERBOARD_SUCCESS,
    (payload: Pick<LeaderboardResultsResponse, "board" | "period" | "results" | "viewer" | "window">) => payload,
  ],
  [SparksActionTypes.GET_LEADERBOARD_FAILURE, (e: unknown) => e],
)();

/** Phase 38 D5: crowns' own independent triad -- see `getLeaderboard`'s comment above. */
export const getLeaderboardCrowns = createAsyncAction(
  [SparksActionTypes.GET_LEADERBOARD_CROWNS, (query: GetLeaderboardQuery) => query],
  [SparksActionTypes.GET_LEADERBOARD_CROWNS_SUCCESS, (crowns: LeaderboardCrown[]) => crowns],
  [SparksActionTypes.GET_LEADERBOARD_CROWNS_FAILURE, (e: unknown) => e],
)();

/**
 * overview-leaderboard-card-plan.md Part 2: the Overview rail card's OWN triad -- calls the SAME
 * `api.getLeaderboard` method as `getLeaderboard`/`getLeaderboardCrowns` above, but on a THIRD,
 * independent action type (own `takeLatest` slot in saga.tsx, own `useLoader` name in the card) so
 * this card's local board/period toggle can never cancel, or be cancelled by, the Leaderboard tab's
 * own fetches. Success payload carries `{ results, viewer }` together -- this card renders the pinned
 * "you" row from `viewer`. Since L2 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md) the tab's own triad carries
 * `viewer` too, and additionally `board`/`period`; see `getLeaderboard`'s own comment above for why
 * those two are load-bearing there and not here.
 */
export const getOverviewLeaderboard = createAsyncAction(
  [SparksActionTypes.GET_OVERVIEW_LEADERBOARD, (query: GetLeaderboardQuery) => query],
  [
    SparksActionTypes.GET_OVERVIEW_LEADERBOARD_SUCCESS,
    (payload: Pick<LeaderboardResultsResponse, "results" | "viewer">) => payload,
  ],
  [SparksActionTypes.GET_OVERVIEW_LEADERBOARD_FAILURE, (e: unknown) => e],
)();

export const setLeaderboardBoard = createAction(SparksActionTypes.SET_LEADERBOARD_BOARD)<LEADERBOARD_BOARD>();
export const setLeaderboardPeriod = createAction(SparksActionTypes.SET_LEADERBOARD_PERIOD)<LEADERBOARD_PERIOD>();
// Phase 38 D6: resets both loading/failure pairs AND the board/period toggles back to their initial
// values -- mirrors clearSparks' own "reset everything" discipline for this tab's own lifecycle.
export const clearLeaderboard = createAction(SparksActionTypes.CLEAR_LEADERBOARD)();

export const clearSparks = createAction(SparksActionTypes.CLEAR_SPARKS)();

// Phase 37 D2: independent of `clearSparks` — a viewed profile's badges are a different lifecycle
// from the logged-in user's own Sparks slice (`summary`/`badges`/`badgeDefinitions`).
export const clearProfileBadges = createAction(SparksActionTypes.CLEAR_PROFILE_BADGES)();

/**
 * redeem-tab-plan.md PR2: the Redeem tab's own read of the logged-in user's counters + ledger, backed
 * by the self-scoped `GET /redemptions/:userId` (PR1 §1b) -- reset by `clearSparks` (see reducer.tsx),
 * the same lifecycle as this logged-in user's other Sparks data. Same all-tuple-arm convention as
 * every other triad in this file (see the file-level comment above `getSparksSummary`).
 */
export const getOwnRedemptionSummary = createAsyncAction(
  [SparksActionTypes.GET_OWN_REDEMPTION_SUMMARY, (userId: number) => userId],
  [SparksActionTypes.GET_OWN_REDEMPTION_SUMMARY_SUCCESS, (summary: UserRedemptionSummary) => summary],
  [SparksActionTypes.GET_OWN_REDEMPTION_SUMMARY_FAILURE, (e: unknown) => e],
)();

/**
 * overview-rebuild-pr5-plan.md: "Your giving week" block's own triad. Self-only, like
 * `getGivingActivity`'s api handler -- there is no `userId` payload, only `tzOffsetMinutes`
 * (`Date#getTimezoneOffset()` convention). `MySparksContainer` captures that value ONCE per mount and
 * reuses the SAME number both as this request's payload and to resolve "today" locally
 * (`givingWeek.ts`'s `resolveTodayIndex`) -- so the two agree by construction, not merely because both
 * happened to read the clock at nearly the same moment. All three arms use the tuple-creator form,
 * matching this file's own established convention (see the file-level comment above
 * `getSparksSummary`).
 */
export const getGivingActivity = createAsyncAction(
  [SparksActionTypes.GET_GIVING_ACTIVITY, (tzOffsetMinutes: number) => tzOffsetMinutes],
  [SparksActionTypes.GET_GIVING_ACTIVITY_SUCCESS, (activity: GivingActivity) => activity],
  [SparksActionTypes.GET_GIVING_ACTIVITY_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w1-rewards-catalog.md: the Redeem catalog's own triad -- a field-for-field copy of
 * `getBadgeDefinitions` above. No request payload -- the catalog doesn't vary by user -- so `request`
 * is a tuple creator returning `undefined`, matching this file's own all-tuple-arm convention (see the
 * file-level comment above `getSparksSummary`). Call site is
 * `SparksActions.getRewardItems.request()` with no argument.
 */
export const getRewardItems = createAsyncAction(
  [SparksActionTypes.GET_REWARD_ITEMS, () => undefined],
  [SparksActionTypes.GET_REWARD_ITEMS_SUCCESS, (items: RewardItem[]) => items],
  [SparksActionTypes.GET_REWARD_ITEMS_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w2-create-request.md: the Redeem tab's own write -- an employee raising a request against
 * `POST /redemption-requests` (A5). `WithCallback` so the container's `cb` can refetch only on success;
 * the success creator strictly typed to the WHOLE `{ request, message }` envelope, never unwrapped to
 * `response.request` -- `errorHandlerMiddleware` reads `payload.message` off any `*_SUCCESS` action, and
 * A5's response carries `SUCCESS_MESSAGE` there.
 *
 * Distinct from the HR-gated `admin/redemptions` write that used to sit alongside this one (an
 * already-decided price, POSTed by HR) -- that write, `createRedemption`, was deleted alongside the
 * mark-consumed flow in 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md. This one POSTs `redemption-requests`, where the
 * price is read server-side from the catalog and the request starts life `Pending`, awaiting HR (W3/W4).
 *
 * 🚨 fix pass H7, for W4: this triad has no reducer arms and no slice fields (see saga.tsx's own
 * comment). That is a property of THIS write specifically -- nothing anywhere subscribes to its
 * result, the container refetches the two summaries wholesale instead -- it is NOT a house rule to
 * copy. W3/W4's approve/deny need reducer-level row REMOVAL from the HR queue and a count decrement in
 * one `.handleAction`, which this triad's shape has nowhere to hang.
 */
export const createRedemptionRequest = createAsyncAction(
  [SparksActionTypes.CREATE_REDEMPTION_REQUEST, (payload: WithCallback<CreateRedemptionRequestDto>) => payload],
  [SparksActionTypes.CREATE_REDEMPTION_REQUEST_SUCCESS, (response: CreateRedemptionRequestSuccess) => response],
  [SparksActionTypes.CREATE_REDEMPTION_REQUEST_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md: the HR queue's own triad -- org-wide. Request payload is the
 * REDUX-side 0-indexed `{ page, limit }` query; `saga.tsx`'s worker tags the success payload with
 * `is_clear` and the request's own `page`/`limit`, which `reducer.tsx`'s `loadedPages`/`lastPageWasFull`
 * rules both depend on -- see that file's own comment. Same all-tuple-arm convention as every other
 * triad in this file (the file-level comment above `getSparksSummary`).
 */
export const getPendingRedemptionRequests = createAsyncAction(
  [SparksActionTypes.GET_PENDING_REDEMPTION_REQUESTS, (query: GetPendingRedemptionRequestsQuery) => query],
  [
    SparksActionTypes.GET_PENDING_REDEMPTION_REQUESTS_SUCCESS,
    (payload: GetPendingRedemptionRequestsSuccess) => payload,
  ],
  [SparksActionTypes.GET_PENDING_REDEMPTION_REQUESTS_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: approve is a one-click write with no body -- `WithCallback` mirrors
 * createRedemptionRequest's own payload shape, but fix pass G moved the page-0 refetch
 * OFF the container's `cb` and onto a dedicated saga watcher
 * (`refetchPendingRedemptionRequestsFirstPageSaga`, saga.tsx, registered on
 * `[approveRedemptionRequest.success, denyRedemptionRequest.success]`) -- RedemptionsContainer's own
 * `handleApproveRequest` dispatches `.request({ id })` with no `cb` at all; `cb` stays on the payload
 * type only for parity with the other triads. Registered on `takeEvery` (saga.tsx), NOT `takeLeading`
 * like those two siblings -- per-row decisions are independent, and a single scalar `clickedId` (W2's
 * createRedemptionRequest shape) cannot discriminate which row a given write belongs to once more than
 * one can be in flight at once (saga.tsx's own comment on this exact point). `.failure`'s payload is
 * tagged with the decided row's `id` -- see `RedemptionRequestDecisionFailure`'s own model comment for
 * why every other triad's bare `(e: unknown) => e` shape does not fit here.
 */
export const approveRedemptionRequest = createAsyncAction(
  [SparksActionTypes.APPROVE_REDEMPTION_REQUEST, (payload: WithCallback<ApproveRedemptionRequestDto>) => payload],
  [SparksActionTypes.APPROVE_REDEMPTION_REQUEST_SUCCESS, (response: ApproveRedemptionRequestSuccess) => response],
  [SparksActionTypes.APPROVE_REDEMPTION_REQUEST_FAILURE, (payload: RedemptionRequestDecisionFailure) => payload],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: deny's own triad -- mirrors approveRedemptionRequest's shape exactly
 * (same takeEvery registration, same tagged failure). The payload differs by a REQUIRED `reason`
 * (architecture §5.6), collected by DenyRedemptionRequestContainer's own overlay before this is ever
 * dispatched -- ✕ cannot be a one-click action the way ✓ is. fix pass G: unlike approve above, this
 * `cb` IS still used, but only by DenyRedemptionRequestContainer to close the overlay on success -- the
 * page-0 refetch it used to also trigger moved to the same saga watcher approve's own comment describes.
 */
export const denyRedemptionRequest = createAsyncAction(
  [SparksActionTypes.DENY_REDEMPTION_REQUEST, (payload: WithCallback<DenyRedemptionRequestDto>) => payload],
  [SparksActionTypes.DENY_REDEMPTION_REQUEST_SUCCESS, (response: DenyRedemptionRequestSuccess) => response],
  [SparksActionTypes.DENY_REDEMPTION_REQUEST_FAILURE, (payload: RedemptionRequestDecisionFailure) => payload],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: the Redemptions TAB badge's own dedicated triad -- deliberately NOT a
 * reuse of getPendingRedemptionRequests above with `limit: 1`. Reusing that triad would route this
 * poll's response through the PANEL's own success handler, which REPLACES `pendingRedemptionRequests`
 * with the single returned row and persists `pendingRedemptionRequestsLimit: 1`/`…LoadedPages: 1`
 * (reducer.tsx), wedging the pager's terminator permanently true (`1 * 1 < total`) and wiping the panel
 * every 30s -- the single most-flagged risk in the whole plan. This triad's OWN success handler writes
 * ONLY `pendingRedemptionRequestsBadgeCount`. No request payload, matching getRewardItems/
 * getBadgeDefinitions' own no-argument precedent above; no `.failure` payload either, matching
 * Recognition/store/actions.tsx's own getPendingCount precedent for a silent background poll (a bare
 * `.failure()` there so a dropped connection never toasts an error every 30s forever).
 */
export const getPendingRedemptionRequestsBadgeCount = createAsyncAction(
  [SparksActionTypes.GET_PENDING_REDEMPTION_REQUESTS_BADGE_COUNT, () => undefined],
  [SparksActionTypes.GET_PENDING_REDEMPTION_REQUESTS_BADGE_COUNT_SUCCESS, (count: number) => count],
  [SparksActionTypes.GET_PENDING_REDEMPTION_REQUESTS_BADGE_COUNT_FAILURE, () => undefined],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md: the org-wide ledger's own triad, over `GET /admin/redemptions` -- a
 * FOURTH hand-written pager (architecture §4.6 applies verbatim; [decision] copy the vertical slice,
 * do not extract a shared hook). phase-50-w6a: request payload is just the redux-side 0-indexed
 * `{ page, limit }` -- the ledger is always org-wide now, no filter alongside it. `saga.tsx`'s worker
 * tags the success payload with `is_clear` and the request's own `page`/`limit`, which `reducer.tsx`'s
 * `loadedPages`/`lastPageWasFull` rules both depend on -- see that file's own comment. Same all-tuple-arm
 * convention as every other triad in this file (the file-level comment above `getSparksSummary`).
 */
export const getRedemptionLedger = createAsyncAction(
  [SparksActionTypes.GET_REDEMPTION_LEDGER, (query: GetRedemptionLedgerQuery) => query],
  [SparksActionTypes.GET_REDEMPTION_LEDGER_SUCCESS, (payload: GetRedemptionLedgerSuccess) => payload],
  [SparksActionTypes.GET_REDEMPTION_LEDGER_FAILURE, (e: unknown) => e],
)();

/**
 * review fix A-C: RedemptionsContainer's mount effect dispatches this immediately before its own
 * page-0 `getRedemptionLedger.request` -- see reducer.tsx's own comment on the handler for exactly
 * which fields it resets and why. Without a reset that runs UNCONDITIONALLY ahead of the request (not
 * merely on that request's `.success`), a page-0 fetch that then FAILS leaves
 * `redemptionLedgerLoadedPages`/`Total`/`Rows`/`LastPageWasFull` all describing a previous load
 * (`.failure` deliberately touches none of them, §4.6 rule 3) -- so a subsequent retry or scroll
 * silently acts on stale data.
 */
export const clearRedemptionLedger = createAction(SparksActionTypes.CLEAR_REDEMPTION_LEDGER)();

/**
 * phase-50-w6b: the three org-wide stat cards' own triad -- a field-for-field copy of `getRewardItems`
 * above (no request payload, org-wide, not scoped to any one person). Same all-tuple-arm convention as
 * every other triad in this file (see the file-level comment above `getSparksSummary`). Call site is
 * `SparksActions.getRedemptionStats.request()` with no argument.
 */
export const getRedemptionStats = createAsyncAction(
  [SparksActionTypes.GET_REDEMPTION_STATS, () => undefined],
  [SparksActionTypes.GET_REDEMPTION_STATS_SUCCESS, (stats: OrgRedemptionStats) => stats],
  [SparksActionTypes.GET_REDEMPTION_STATS_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: the "Spread your Sparks" section's own triad -- a field-for-field
 * copy of `getRewardItems`/`getRedemptionStats` above (no request payload -- the endpoint takes no query
 * params at all, see actions.tsx's own comment on `GetLeaderboardCollaboratorsResponse` -- viewer-scoped
 * via the token, not a `userId` argument). Same all-tuple-arm convention as every other triad in this
 * file (see the file-level comment above `getSparksSummary`). Call site is
 * `SparksActions.getLeaderboardCollaborators.request()` with no argument.
 */
export const getLeaderboardCollaborators = createAsyncAction(
  [SparksActionTypes.GET_LEADERBOARD_COLLABORATORS, () => undefined],
  [SparksActionTypes.GET_LEADERBOARD_COLLABORATORS_SUCCESS, (collaborators: SuggestedCollaborator[]) => collaborators],
  [SparksActionTypes.GET_LEADERBOARD_COLLABORATORS_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: the "Your all-time" card's own rank -- ALWAYS `board=received`,
 * `period=All-time`, regardless of which board/period the tab's own toggles are on (`getLeaderboard`
 * above). No request payload: the query is fixed, not caller-supplied, so a call site cannot
 * accidentally pass the wrong board/period the way a bare `GetLeaderboardQuery` argument would allow.
 * Success payload is a bare `number | null` (the viewer's own `rank`, or `null` when they have no score
 * at all in ALL_TIME) -- `saga.tsx`'s worker is what narrows the full response down to that one field.
 */
export const getAllTimeRank = createAsyncAction(
  [SparksActionTypes.GET_ALL_TIME_RANK, () => undefined],
  [SparksActionTypes.GET_ALL_TIME_RANK_SUCCESS, (rank: number | null) => rank],
  [SparksActionTypes.GET_ALL_TIME_RANK_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: the same card's other two figures (best quarter, first Spark
 * received) -- `GET /recognitions/leaderboard/all-time-summary`, no request payload (viewer-scoped via
 * the token), mirroring `getLeaderboardCollaborators`'s own no-argument precedent above.
 */
export const getAllTimeSummary = createAsyncAction(
  [SparksActionTypes.GET_ALL_TIME_SUMMARY, () => undefined],
  [SparksActionTypes.GET_ALL_TIME_SUMMARY_SUCCESS, (summary: AllTimeSummary) => summary],
  [SparksActionTypes.GET_ALL_TIME_SUMMARY_FAILURE, (e: unknown) => e],
)();

/**
 * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: the "a person's Sparks" drawer's own triad. `request`'s payload
 * is the subject's userId; saga.tsx's worker fetches FOUR independent endpoints in parallel (`all`) and
 * combines them into one `PersonSparks` object -- all four must succeed, or the whole triad fails (the
 * drawer shows one error line, never a half-filled drawer).
 *
 * Both `.success` and `.failure` are tagged with the SAME `userId` the request was made for (mirroring
 * `getProfileBadges`' own `{ userId, badges }` tagging above) -- this is what lets reducer.tsx's
 * stale-subject guard drop a response for whoever was open BEFORE the current person (opening person A,
 * then B, before A's four calls land: A's late response must not overwrite B's slot). All three arms use
 * the tuple-creator form, matching this file's own all-tuple-arm convention (the file-level comment
 * above `getSparksSummary`).
 */
export const getPersonSparks = createAsyncAction(
  [SparksActionTypes.GET_PERSON_SPARKS, (userId: number) => userId],
  [SparksActionTypes.GET_PERSON_SPARKS_SUCCESS, (payload: { userId: number; data: PersonSparks }) => payload],
  [SparksActionTypes.GET_PERSON_SPARKS_FAILURE, (payload: { userId: number; error: unknown }) => payload],
)();

/** 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md §6: dispatched on the drawer's close -- resets the whole
 *  personSparks slot to its initial value (reducer.tsx). */
export const clearPersonSparks = createAction(SparksActionTypes.CLEAR_PERSON_SPARKS)();
